/**
 * Admin requests on a user's setup (User management → Reset setup / Resend
 * consent / Reset device permissions / per-step "Ask again"). Stored on the
 * Users row as the top-level `setupRequests` map — never in `settingsJson` —
 * and stamped server-side only.
 */

import { isDeviceSetupStep, type SetupStepId } from './requirements'

export type SetupRequests = {
  /** Setup runs again from the start on every device that finished before this. */
  setupResetAt?: string
  /** The call-recording question is asked again; the current answer stays until they reply. */
  consentRequestedAt?: string
  /** The device-permission steps are walked again on every device (not the call-recording answer). */
  permissionsResetAt?: string
  /** Single steps an admin asked about again ("Ask again" on a permission row). */
  stepsRequestedAt?: Partial<Record<SetupStepId, string>>
}

/** Push `data.type` for all admin requests (content-free alert). */
export const SETUP_REQUEST_PUSH_TYPE = 'SETUP_REQUEST'
export type SetupRequestKind = 'reset' | 'consent' | 'permissions' | 'step'

/** Deep link that opens setup (optionally at a step): `/?setup=<step>`. */
export const SETUP_DEEP_LINK_PARAM = 'setup'

/** Storage prefix of a per-step request inside the `setupRequests` map (`step.microphone`). */
export const SETUP_STEP_REQUEST_PREFIX = 'step.'

export const SETUP_REQUEST_PUSH_COPY: Record<SetupRequestKind, { title: string; body: string }> = {
  reset: {
    title: 'Ascend Messenger',
    body: 'Please open Ascend Messenger to check your set up.',
  },
  consent: {
    title: 'Ascend Messenger',
    body: 'Please answer a question about call recording. Tap this message to answer.',
  },
  permissions: {
    title: 'Ascend Messenger',
    body: 'Please check some settings for Ascend Messenger. Tap this message to start.',
  },
  step: {
    title: 'Ascend Messenger',
    body: 'Please check one thing in your set up. Tap this message to do it.',
  },
}

export function isSetupRequestKind(value: unknown): value is SetupRequestKind {
  return value === 'reset' || value === 'consent' || value === 'permissions' || value === 'step'
}

function isoOrUndefined(value: unknown): string | undefined {
  if (typeof value !== 'string' || value.length > 40) return undefined
  const ms = Date.parse(value)
  return Number.isFinite(ms) ? new Date(ms).toISOString() : undefined
}

/** Reads the stored map (object or AWSJSON string); unknown keys and bad dates are dropped. */
export function parseSetupRequests(raw: unknown): SetupRequests {
  let value: unknown = raw
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value)
    } catch {
      return {}
    }
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  const v = value as Record<string, unknown>
  const out: SetupRequests = {}
  const setupResetAt = isoOrUndefined(v.setupResetAt)
  if (setupResetAt) out.setupResetAt = setupResetAt
  const consentRequestedAt = isoOrUndefined(v.consentRequestedAt)
  if (consentRequestedAt) out.consentRequestedAt = consentRequestedAt
  const permissionsResetAt = isoOrUndefined(v.permissionsResetAt)
  if (permissionsResetAt) out.permissionsResetAt = permissionsResetAt
  for (const [key, rawValue] of Object.entries(v)) {
    if (!key.startsWith(SETUP_STEP_REQUEST_PREFIX)) continue
    const step = key.slice(SETUP_STEP_REQUEST_PREFIX.length)
    const at = isoOrUndefined(rawValue)
    if (!at || !isDeviceSetupStep(step)) continue
    out.stepsRequestedAt = { ...out.stepsRequestedAt, [step]: at }
  }
  return out
}

function ms(value: string | null | undefined): number {
  if (!value) return Number.NaN
  return Date.parse(value)
}

/**
 * An admin asked again and the person hasn't answered since. Answering
 * re-stamps `recordingPolicyConsentAt`, so the request clears itself.
 */
export function isConsentRequestPending(
  user:
    | { setupRequests?: unknown; recordingPolicyConsentAt?: string | null }
    | null
    | undefined,
): boolean {
  if (!user) return false
  const requested = ms(parseSetupRequests(user.setupRequests).consentRequestedAt)
  if (!Number.isFinite(requested)) return false
  const answered = ms(user.recordingPolicyConsentAt)
  return !Number.isFinite(answered) || requested > answered
}

/**
 * An admin reset this device hasn't honoured yet, so first-run setup shows
 * again. Compares server timestamps only: the device stores the
 * `setupResetAt` it last honoured (when it finishes or dismisses first-run
 * setup) and passes it here, so a device clock never hides a reset.
 */
export function isSetupResetPending(
  honouredResetAt: string | null | undefined,
  user: { setupRequests?: unknown } | null | undefined,
): boolean {
  if (!user) return false
  const reset = ms(parseSetupRequests(user.setupRequests).setupResetAt)
  if (!Number.isFinite(reset)) return false
  const honoured = ms(honouredResetAt)
  return !Number.isFinite(honoured) || reset > honoured
}

/**
 * An admin "Reset device permissions" this device hasn't walked yet. Honoured
 * (stored locally) when the device finishes the permissions walk — same
 * server-timestamp comparison as `isSetupResetPending`.
 */
export function isPermissionsResetPending(
  honouredPermissionsResetAt: string | null | undefined,
  user: { setupRequests?: unknown } | null | undefined,
): boolean {
  if (!user) return false
  const reset = ms(parseSetupRequests(user.setupRequests).permissionsResetAt)
  if (!Number.isFinite(reset)) return false
  const honoured = ms(honouredPermissionsResetAt)
  return !Number.isFinite(honoured) || reset > honoured
}

/**
 * Per-step "Ask again" requests this device hasn't shown yet: step → the
 * request stamp to store once it has been put in front of the person
 * (`shownFor[step]` keeps that exact stamp, like the consent request).
 */
export function pendingStepRequests(
  shownFor: Partial<Record<SetupStepId, string>> | null | undefined,
  user: { setupRequests?: unknown } | null | undefined,
): Partial<Record<SetupStepId, string>> {
  const requested = parseSetupRequests(user?.setupRequests).stepsRequestedAt
  if (!requested) return {}
  const out: Partial<Record<SetupStepId, string>> = {}
  for (const [step, at] of Object.entries(requested) as [SetupStepId, string][]) {
    if (shownFor?.[step] !== at) out[step] = at
  }
  return out
}
